'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  HDR_CONTACT_EMAIL,
  HDR_PRICING_COPY,
  detectBillingCurrency,
  formatHdrMonthlyPrice,
  formatHdrPerImagePrice,
  getActiveHdrPlans,
  getRecommendedHdrPlan,
  persistBillingCurrency,
  readPersistedBillingCurrency,
  type HdrCurrency,
  type HdrPlan,
} from '@rehub/pricing';
import CurrencySelector from './CurrencySelector';

export type HdrPricingTheme = 'dark' | 'light';

export interface HdrPricingSliderProps {
  plans?: HdrPlan[];
  currency?: HdrCurrency;
  remaining?: number | null;
  /** User's current plan allowance. Remaining is only shown when this matches the selected volume. */
  currentAllowance?: number | null;
  /** Optional controlled plan. When this changes, the slider jumps to that volume. */
  planId?: string;
  theme?: HdrPricingTheme;
  className?: string;
  ctaHref?: string;
  contactHref?: string;
  /** Hide the built-in currency toggle when the parent already shows one. */
  showCurrencySelector?: boolean;
  onCta?: (plan: HdrPlan, currency: HdrCurrency) => void;
  onPlanChange?: (plan: HdrPlan, currency: HdrCurrency) => void;
  onView?: (plan: HdrPlan, currency: HdrCurrency) => void;
}

const THEMES: Record<HdrPricingTheme, {
  wrap: string;
  label: string;
  value: string;
  muted: string;
  track: string;
  fill: string;
  thumb: string;
  tick: string;
  tickActive: string;
  pill: string;
  cta: string;
  ctaSecondary: string;
  remaining: string;
}> = {
  dark: {
    wrap: 'hdr-pricing-panel rounded-3xl p-6 sm:p-8',
    label: 'text-slate-400',
    value: 'text-white',
    muted: 'text-slate-400',
    track: 'bg-slate-800',
    fill: 'bg-sky-500',
    thumb: 'border-sky-300 bg-white',
    tick: 'text-slate-500',
    tickActive: 'text-sky-300',
    pill: 'border-slate-800 bg-slate-900 text-slate-300',
    cta: 'bg-sky-600 text-white hover:bg-sky-500',
    ctaSecondary: 'border border-slate-700 bg-slate-900 text-slate-200 hover:border-slate-500',
    remaining: 'text-sky-300',
  },
  light: {
    wrap: 'hdr-pricing-panel hdr-pricing-panel-light rounded-3xl p-6 sm:p-8',
    label: 'text-slate-500',
    value: 'text-slate-900',
    muted: 'text-slate-500',
    track: 'bg-slate-200',
    fill: 'bg-sky-600',
    thumb: 'border-sky-600 bg-white',
    tick: 'text-slate-400',
    tickActive: 'text-sky-700',
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    cta: 'bg-sky-600 text-white hover:bg-sky-500',
    ctaSecondary: 'border border-slate-300 bg-white text-slate-700 hover:border-slate-400',
    remaining: 'text-sky-700',
  },
};

export default function HdrPricingSlider({
  plans: plansProp,
  currency: currencyProp,
  remaining = null,
  currentAllowance = null,
  planId,
  theme = 'dark',
  className = '',
  ctaHref,
  contactHref = `mailto:${HDR_CONTACT_EMAIL}`,
  showCurrencySelector = true,
  onCta,
  onPlanChange,
  onView,
}: HdrPricingSliderProps) {
  const plans = useMemo(
    () => getActiveHdrPlans(plansProp?.length ? plansProp : undefined),
    [plansProp],
  );
  const recommended = useMemo(() => getRecommendedHdrPlan(plans), [plans]);
  const [index, setIndex] = useState(() => {
    const requested = planId ? plans.findIndex((p) => p.id === planId) : -1;
    if (requested >= 0) return requested;
    const next = plans.findIndex((p) => p.id === recommended.id);
    return next >= 0 ? next : 0;
  });
  // Default to GBP on first paint so SSR/client markup match. Detect after mount.
  const [currency, setCurrency] = useState<HdrCurrency>(currencyProp || 'GBP');

  useEffect(() => {
    if (currencyProp) {
      setCurrency(currencyProp);
      return;
    }
    setCurrency(readPersistedBillingCurrency() || detectBillingCurrency());
  }, [currencyProp]);

  useEffect(() => {
    const next = plans.findIndex((p) => p.id === recommended.id);
    setIndex((current) => (plans[current] ? current : Math.max(0, next)));
  }, [plans, recommended.id]);

  useEffect(() => {
    if (!planId) return;
    const next = plans.findIndex((p) => p.id === planId);
    if (next < 0) return;
    setIndex((current) => (current === next ? current : next));
  }, [planId, plans]);

  const plan = plans[index] || recommended;
  const styles = THEMES[theme];
  const percent = plans.length > 1 ? (index / (plans.length - 1)) * 100 : 0;
  const accentHue = 187 + (percent / 100) * 125;
  const perImage = formatHdrPerImagePrice(plan, currency);
  const priceLabel = formatHdrMonthlyPrice(plan, currency);
  const ctaLabel = plan.isContact ? HDR_PRICING_COPY.ctaContact : HDR_PRICING_COPY.ctaStart;
  const href = plan.isContact ? contactHref : ctaHref;

  useEffect(() => {
    onView?.(plan, currency);
    // Fire once when the slider first mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const trackRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const draggingRef = useRef(false);
  const plansRef = useRef(plans);
  const currencyRef = useRef(currency);
  const onPlanChangeRef = useRef(onPlanChange);
  plansRef.current = plans;
  currencyRef.current = currency;
  onPlanChangeRef.current = onPlanChange;

  const selectIndex = (next: number) => {
    const clamped = Math.max(0, Math.min(plans.length - 1, next));
    setIndex(clamped);
    const selected = plans[clamped];
    if (selected) onPlanChange?.(selected, currency);
  };

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;

    const selectFromClientX = (clientX: number) => {
      const nextPlans = plansRef.current;
      if (nextPlans.length < 2) return;
      const rect = el.getBoundingClientRect();
      if (rect.width <= 0) return;
      const t = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
      const next = Math.round(t * (nextPlans.length - 1));
      const clamped = Math.max(0, Math.min(nextPlans.length - 1, next));
      setIndex(clamped);
      const selected = nextPlans[clamped];
      if (selected) onPlanChangeRef.current?.(selected, currencyRef.current);
    };

    const onDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      event.preventDefault();
      draggingRef.current = true;
      selectFromClientX(event.clientX);
      inputRef.current?.focus({ preventScroll: true });
    };
    const onMove = (event: PointerEvent) => {
      if (!draggingRef.current) return;
      selectFromClientX(event.clientX);
    };
    const onUp = () => {
      draggingRef.current = false;
    };

    el.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const switchCurrency = (next: HdrCurrency) => {
    setCurrency(next);
    persistBillingCurrency(next);
    onPlanChange?.(plan, next);
  };

  const handleCta = () => {
    onCta?.(plan, currency);
  };

  return (
    <div className={`${styles.wrap} ${className}`}>
      <style>{`
        .hdr-pricing-panel {
          position: relative;
          overflow: hidden;
          isolation: isolate;
          border: 1px solid rgba(56, 189, 248, 0.45);
          background:
            radial-gradient(110% 90% at 0% -5%, rgba(34, 211, 238, 0.55), transparent 58%),
            radial-gradient(80% 70% at 55% 0%, rgba(99, 102, 241, 0.22), transparent 50%),
            radial-gradient(100% 80% at 100% 110%, rgba(232, 121, 249, 0.42), transparent 55%),
            linear-gradient(160deg, #16324f 0%, #10182f 42%, #2a1248 100%);
          box-shadow:
            0 0 0 1px rgba(232, 121, 249, 0.18),
            0 18px 50px rgba(14, 165, 233, 0.18),
            0 28px 70px rgba(88, 28, 135, 0.28),
            inset 0 1px 0 rgba(255, 255, 255, 0.12);
        }
        .hdr-pricing-panel::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: inherit;
          padding: 1.5px;
          background: linear-gradient(135deg, rgba(34, 211, 238, 0.95), rgba(56, 189, 248, 0.45), rgba(167, 139, 250, 0.7), rgba(232, 121, 249, 0.95));
          -webkit-mask:
            linear-gradient(#fff 0 0) content-box,
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          pointer-events: none;
        }
        .hdr-pricing-panel-light {
          border-color: rgba(14, 165, 233, 0.4);
          background:
            radial-gradient(110% 90% at 0% -5%, rgba(14, 165, 233, 0.28), transparent 58%),
            radial-gradient(80% 70% at 55% 0%, rgba(99, 102, 241, 0.12), transparent 50%),
            radial-gradient(100% 80% at 100% 110%, rgba(192, 132, 252, 0.28), transparent 55%),
            linear-gradient(160deg, #ffffff 0%, #eef6ff 48%, #f3e8ff 100%);
          box-shadow:
            0 18px 40px rgba(14, 165, 233, 0.12),
            inset 0 1px 0 rgba(255, 255, 255, 0.9);
        }
        .hdr-pricing-panel-light::before {
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.85), rgba(99, 102, 241, 0.4), rgba(192, 132, 252, 0.85));
        }
        .hdr-pricing-track {
          container-type: inline-size;
        }
        .hdr-pricing-fill {
          background-image: linear-gradient(90deg, #22d3ee 0%, #38bdf8 28%, #818cf8 62%, #e879f9 100%);
          background-size: 100% 100%;
          background-size: 100cqw 100%;
          background-repeat: no-repeat;
          box-shadow:
            0 0 10px rgba(34, 211, 238, 0.45),
            0 0 22px rgba(232, 121, 249, 0.35);
        }
        .hdr-pricing-thumb {
          width: 22px;
          height: 22px;
          transform: translate(-50%, -50%);
          border-radius: 9999px;
          background: #fff;
        }
        .hdr-pricing-cta {
          position: relative;
          overflow: hidden;
        }
        .hdr-pricing-cta::after {
          content: '';
          position: absolute;
          inset: 0;
          background: linear-gradient(
            105deg,
            transparent 0%,
            transparent 38%,
            rgba(255, 255, 255, 0.08) 46%,
            rgba(255, 255, 255, 0.55) 50%,
            rgba(255, 255, 255, 0.08) 54%,
            transparent 62%,
            transparent 100%
          );
          transform: translateX(-120%);
          animation: hdr-pricing-shine 2.8s ease-in-out infinite;
          pointer-events: none;
        }
        @keyframes hdr-pricing-shine {
          0%, 18% { transform: translateX(-120%); }
          58%, 100% { transform: translateX(120%); }
        }
        .hdr-pricing-slider-range {
          margin: 0;
          -webkit-appearance: none;
          appearance: none;
          pointer-events: none;
        }
        .hdr-pricing-slider-range::-webkit-slider-runnable-track {
          background: transparent;
          height: 44px;
        }
        .hdr-pricing-slider-range::-moz-range-track {
          background: transparent;
          border: 0;
          height: 44px;
        }
        .hdr-pricing-slider-range::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          height: 44px;
          width: 44px;
          border: 0;
          background: transparent;
          box-shadow: none;
        }
        .hdr-pricing-slider-range::-moz-range-thumb {
          height: 44px;
          width: 44px;
          border: 0;
          background: transparent;
          box-shadow: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .hdr-pricing-cta::after {
            animation: none !important;
            display: none;
          }
        }
      `}</style>
      <div className="flex items-start justify-between gap-3 sm:gap-6">
        <div className="min-w-0 flex-1">
          <p className={`text-sm font-medium ${styles.label}`}>Monthly volume</p>
          <p className={`mt-1 min-h-[2.5rem] text-3xl font-semibold tracking-tight tabular-nums sm:min-h-[2.75rem] sm:text-4xl ${styles.value}`}>
            {plan.isContact
              ? '5,000+'
              : plan.imageAllowance.toLocaleString('en-GB')}
          </p>
          <p className={`mt-1 text-sm ${styles.muted}`}>HDR Credits / month</p>
        </div>
        {showCurrencySelector ? (
          <div className="shrink-0">
            <CurrencySelector
              value={currency}
              showLabel
              onChange={switchCurrency}
              activeClassName={styles.cta}
              inactiveClassName={styles.muted}
            />
          </div>
        ) : null}
      </div>

      <div className="mt-8 px-3">
        <div className="hdr-pricing-track relative">
          <div
            ref={trackRef}
            className="relative h-11 cursor-pointer touch-none select-none"
          >
            <div className={`pointer-events-none absolute top-1/2 h-2 w-full -translate-y-1/2 rounded-full ${styles.track}`} />
            <div
              className="hdr-pricing-fill pointer-events-none absolute top-1/2 h-2 -translate-y-1/2 rounded-full"
              style={{ width: `${percent}%` }}
            />
            <div
              className="hdr-pricing-thumb pointer-events-none absolute top-1/2 z-20"
              style={{
                left: `${percent}%`,
                border: `2px solid hsl(${accentHue}, 90%, 62%)`,
                boxShadow: `0 0 0 5px hsla(${accentHue}, 90%, 60%, 0.22), 0 0 18px hsla(${accentHue}, 90%, 60%, 0.55)`,
              }}
              aria-hidden
            />
            <input
              ref={inputRef}
              type="range"
              min={0}
              max={Math.max(0, plans.length - 1)}
              step={1}
              value={index}
              aria-label="HDR image volume"
              onChange={(event) => selectIndex(Number(event.target.value))}
              className="hdr-pricing-slider-range absolute inset-0 z-10 w-full appearance-none bg-transparent"
            />
          </div>
          <div className="relative mt-4 h-5">
            {plans.map((item, i) => {
              const left = plans.length > 1 ? (i / (plans.length - 1)) * 100 : 0;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => selectIndex(i)}
                  style={{ left: `${left}%` }}
                  className={`hdr-pricing-tick absolute top-0 -translate-x-1/2 whitespace-nowrap px-0 py-0 text-[11px] font-semibold uppercase tracking-wide ${
                    i === index ? styles.tickActive : styles.tick
                  }`}
                >
                  {item.sliderLabel}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        <div>
          <p className={`text-sm ${styles.label}`}>Monthly price</p>
          <p className={`mt-1 min-h-[2.5rem] text-3xl font-semibold tabular-nums whitespace-nowrap ${styles.value}`}>
            {priceLabel}
          </p>
        </div>
        <div>
          <p className={`text-sm ${styles.label}`}>Effective price</p>
          <p className={`mt-1 min-h-[2.25rem] text-2xl font-semibold tabular-nums whitespace-nowrap ${styles.value}`}>
            {perImage || 'Custom volume'}
          </p>
        </div>
      </div>

      <div className="mt-4 min-h-[1.75rem]">
        {plan.isRecommended && (
          <p className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${styles.pill}`}>
            Recommended for most photographers
          </p>
        )}
      </div>

      {remaining != null && remaining >= 0 && currentAllowance != null && currentAllowance === plan.imageAllowance && (
        <p className={`mt-4 text-sm font-medium ${styles.remaining}`}>
          {HDR_PRICING_COPY.remainingOf(remaining, currentAllowance)}
        </p>
      )}

      <div className="mt-8">
        {href ? (
          <a
            href={href}
            onClick={handleCta}
            className={`${plan.isContact ? '' : 'hdr-pricing-cta '}inline-flex w-full items-center justify-center rounded-2xl px-5 py-3.5 text-base font-semibold transition-colors ${styles.cta}`}
          >
            <span className="relative z-10">{ctaLabel}</span>
          </a>
        ) : (
          <button
            type="button"
            onClick={handleCta}
            className={`${plan.isContact ? '' : 'hdr-pricing-cta '}inline-flex w-full items-center justify-center rounded-2xl px-5 py-3.5 text-base font-semibold transition-colors ${styles.cta}`}
          >
            <span className="relative z-10">{ctaLabel}</span>
          </button>
        )}
      </div>
    </div>
  );
}
